import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import humanTime from 'flarum/common/utils/humanTime';
import { loadStates, countryName } from '../../common/geo';
import { data } from '../../common/community';

/**
 * Guests on My Community: "Find your people". A search for any city, state or country, popular places to tap,
 * and a preview of the chosen place (its latest discussions, and for a Nigerian state its governor and ranking),
 * with a join button for that place. Before a place is chosen, "Happening now" shows the latest three
 * discussions from location tags. Everything shown is real: nothing here is made up or guessed.
 */

// Popular places, Nigeria first (matched by name against the location tags; any not found are left out)
const POPULAR = ['Lagos', 'Abuja', 'Kano', 'Rivers', 'Oyo', 'Kaduna', 'Toronto', 'London', 'Houston'];
export default class CommunityJoin extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.query = '';
    this.states = [];
    this.governors = [];
    this.place = null; // the chosen place: { name, sub, slug, kind }
    this.preview = {}; // slug -> discussions (null while loading)
    this.happening = null;
    const api = app.forum.attribute('apiUrl');
    loadStates().then(s => this.states = s || []).catch(() => {}).then(() => {
      m.redraw();
      this.loadHappening();
    });
    app.request({
      method: 'GET',
      url: api + '/voucha-community/governors',
      errorHandler: () => {}
    }).then(r => {
      this.governors = r && r.governors || [];
      m.redraw();
    }).catch(() => {});
  }

  /** Every place with a tag: countries, their cities, and the states */
  places() {
    const list = [];
    Object.entries(data().cities || {}).forEach(([code, c]) => {
      if (c.tag) list.push({
        name: countryName(code),
        sub: 'Country',
        slug: c.tag,
        kind: 'country'
      });
      (c.cities || []).forEach(city => city.slug && list.push({
        name: city.name,
        sub: countryName(code),
        slug: city.slug,
        kind: 'city'
      }));
    });
    this.states.forEach(s => {
      if (s.tagSlug) list.push({
        name: s.name,
        sub: 'Nigeria',
        slug: s.tagSlug,
        kind: 'state',
        stateName: s.name
      });
    });
    return list;
  }
  popular() {
    const all = this.places();
    const out = [];
    POPULAR.forEach(n => {
      const key = n.toLowerCase();
      const p = all.find(x => x.name.toLowerCase() === key) || all.find(x => x.name.toLowerCase().startsWith(key)) || all.find(x => x.name.toLowerCase().includes(key));
      if (p && !out.includes(p)) out.push(p);
    });
    return out;
  }

  /** A tag's discussion count, when the tag is loaded (the sidebar loads them all) */
  count(slug) {
    const tag = (app.store.all('tags') || []).find(t => t.slug() === slug);
    return tag && tag.discussionCount ? tag.discussionCount() : null;
  }
  latest(slugs, limit) {
    return app.store.find('discussions', {
      filter: {
        tag: slugs
      },
      sort: '-lastPostedAt',
      include: 'tags',
      page: {
        limit
      }
    }).then(list => Array.from(list || [])).catch(() => []);
  }

  /** The latest discussions in any location tag (states and cities) */
  loadHappening() {
    const slugs = this.places().filter(p => p.kind !== 'country').map(p => p.slug);
    if (!slugs.length) {
      this.happening = [];
      return;
    }
    this.latest(slugs.join(','), 3).then(list => {
      this.happening = list;
      m.redraw();
    });
  }
  choose(p) {
    this.place = p;
    this.query = '';
    if (!(p.slug in this.preview)) {
      this.preview[p.slug] = null;
      this.latest(p.slug, 3).then(list => {
        this.preview[p.slug] = list;
        m.redraw();
      });
    }
  }
  logIn(e) {
    if (e) e.preventDefault();
    const button = document.querySelector('.item-logIn .Button, .item-logIn button');
    if (button) button.click();
  }
  signUp() {
    const button = document.querySelector('.item-signUp .Button, .item-signUp button');
    if (button) button.click();
  }
  go(url) {
    return e => {
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      m.route.set(url);
    };
  }

  /** Where a discussion's location tags place it: "Lagos", "Canada · Toronto" */
  where(d) {
    const tags = (d.tags && d.tags() || []).filter(Boolean);
    const slugs = new Set(this.places().map(p => p.slug));
    const loc = tags.filter(t => slugs.has(t.slug()));
    const city = loc.find(t => t.parent && t.parent());
    if (city) return [city.parent().name(), city.name()].join(' · ');
    return loc[0] ? loc[0].name() : '';
  }
  discussionList(list, showPlace) {
    return m("ul", {
      className: "VouchaJoin-list"
    }, list.map(d => {
      const url = app.route.discussion(d);
      const replies = Math.max(0, (d.commentCount() || 1) - 1);
      return m("li", null, m("a", {
        href: url,
        onclick: this.go(url)
      }, showPlace && this.where(d) ? m("small", {
        className: "VouchaJoin-listPlace"
      }, this.where(d)) : null, m("strong", null, d.title()), m("span", {
        className: "VouchaJoin-listMeta"
      }, m("i", {
        className: "far fa-comment",
        "aria-hidden": "true"
      }), " ", replies, " ", replies === 1 ? 'reply' : 'replies', d.lastPostedAt() ? m("span", null, " \xB7 ", humanTime(d.lastPostedAt())) : null)));
    }));
  }
  previewView() {
    const p = this.place;
    const list = this.preview[p.slug];
    const tagUrl = app.route('tag', {
      tags: p.slug
    });
    const n = this.count(p.slug);
    const gov = p.kind === 'state' ? this.governors.find(g => g.tagSlug === p.slug) : null;
    return m("section", {
      className: "VouchaJoin-preview",
      "aria-live": "polite"
    }, m("div", {
      className: "VouchaJoin-previewHead"
    }, m("div", null, m("h3", null, p.name), m("small", null, p.sub, n ? ' · ' + n.toLocaleString('en-US') + (n === 1 ? ' discussion' : ' discussions') : '')), m("a", {
      className: "VouchaJoin-open",
      href: tagUrl,
      onclick: this.go(tagUrl)
    }, "Open ", p.name, " \u2192")), gov && gov.name ? m("a", {
      className: "VouchaJoin-gov",
      href: "/community/governors",
      onclick: this.go('/community/governors')
    }, gov.photo ? m("img", {
      src: gov.photo,
      alt: "",
      loading: "lazy"
    }) : m("i", {
      className: "fas fa-landmark",
      "aria-hidden": "true"
    }), m("span", null, m("strong", null, gov.name), m("small", null, gov.role || 'Governor', gov.party ? ' · ' + gov.party : '')), gov.rank ? m("span", {
      className: "VouchaJoin-rank"
    }, m("b", null, "#", gov.rank), " of ", gov.of) : null) : null, list === null ? m("p", {
      className: "VouchaJoin-note"
    }, "Loading the latest from ", p.name, "\u2026") : list.length ? this.discussionList(list, false) : m("p", {
      className: "VouchaJoin-note"
    }, "No discussions in ", p.name, " yet. Be the first to start one."), m("button", {
      className: "Button Button--primary VouchaJoin-cta",
      onclick: () => this.signUp()
    }, "Join the ", p.name, " community"));
  }
  view() {
    const q = this.query.trim().toLowerCase();
    const results = q ? this.places().filter(p => p.name.toLowerCase().includes(q)).slice(0, 8) : [];
    const popular = this.popular();
    return m("div", {
      className: "VouchaJoin"
    }, m("h2", null, "Find your people"), m("p", null, "Local talk, real figures and people who know the place: in your city, your state or wherever you live."), m("div", {
      className: "VouchaJoin-search"
    }, m("i", {
      className: "fas fa-search",
      "aria-hidden": "true"
    }), m("input", {
      className: "FormControl",
      placeholder: "Your city, state or country",
      "aria-label": "Find a city, state or country",
      value: this.query,
      oninput: e => this.query = e.target.value
    }), results.length ? m("ul", {
      className: "VouchaJoin-results"
    }, results.map(r => m("li", null, m("button", {
      type: "button",
      onclick: () => this.choose(r)
    }, m("strong", null, r.name), " ", m("small", null, r.sub))))) : q ? m("ul", {
      className: "VouchaJoin-results"
    }, m("li", {
      className: "is-empty"
    }, "No match yet. Join and tell us where you are.")) : null), popular.length ? m("div", {
      className: "VouchaJoin-chips",
      role: "group",
      "aria-label": "Popular places"
    }, popular.map(p => {
      const n = this.count(p.slug);
      return m("button", {
        type: "button",
        className: 'VouchaJoin-chip' + (this.place && this.place.slug === p.slug ? ' is-on' : ''),
        onclick: () => this.choose(p)
      }, p.name, n ? m("small", null, n.toLocaleString('en-US')) : null);
    })) : null, this.place ? this.previewView() : m("section", {
      className: "VouchaJoin-preview"
    }, m("div", {
      className: "VouchaJoin-previewHead"
    }, m("h3", null, "Happening now")), this.happening === null ? m("p", {
      className: "VouchaJoin-note"
    }, "Loading\u2026") : this.happening.length ? this.discussionList(this.happening, true) : m("p", {
      className: "VouchaJoin-note"
    }, "Pick a place above to see what people there are talking about."), m("button", {
      className: "Button Button--primary VouchaJoin-cta",
      onclick: () => this.signUp()
    }, "Join Voucha")), m("p", {
      className: "VouchaJoin-small"
    }, "Takes about 30 seconds \xB7", ' ', m("a", {
      href: "/login",
      onclick: e => this.logIn(e)
    }, "Log in")), m("div", {
      className: "VouchaJoin-get"
    }, m("h3", null, "What you get"), m("div", {
      className: "VouchaJoin-getGrid"
    }, m("div", {
      className: "VouchaJoin-getCard"
    }, m("i", {
      className: "fas fa-comments",
      "aria-hidden": "true"
    }), m("strong", null, "Local talk"), m("span", null, "Rent, jobs, prices and meetups, from people who live there.")), m("a", {
      className: "VouchaJoin-getCard",
      href: "/community/governors",
      onclick: this.go('/community/governors')
    }, m("i", {
      className: "fas fa-chart-bar",
      "aria-hidden": "true"
    }), m("strong", null, "Your state in numbers"), m("span", null, "All 36 states and the FCT ranked, with their governors and finances.")), m("a", {
      className: "VouchaJoin-getCard",
      href: "/events",
      onclick: this.go('/events')
    }, m("i", {
      className: "fas fa-calendar-days",
      "aria-hidden": "true"
    }), m("strong", null, "Events near you"), m("span", null, "Meetups, talks and gatherings where you live, in person and online.")))));
  }
}
flarum.reg.add('voucha-community', 'forum/components/CommunityJoin', CommunityJoin);